/**
 * Every sentence on the field-mapping screen, in one file so the wording can be reviewed in one place.
 *
 * Two things are deliberately not here. The book-read consent text is built in `book-read-consent`
 * because it is sent back and stored word for word as the consent record. Messages the server
 * returns are shown as the server wrote them.
 *
 * The vocabulary is fixed: Waterlily "syncs" contacts and "refreshes" the field list. A pause while
 * something happens ends in a single ellipsis character.
 */
import type { Prospecting } from '@client/api/types';
import type { FieldMapGroupKey } from './field-map-groups';

export const SCALE_LABELS: Record<NonNullable<Prospecting.FieldSemantics['scale']>, string> = {
  ones: 'Dollars',
  thousands: 'Thousands of dollars',
  millions: 'Millions of dollars',
};

const MATCH_REASONS: Record<string, string> = {
  name: 'Matched by field name.',
  alias: 'Matched by field name.',
  label: 'Matched by field label.',
  words: 'Matched by the words in its name.',
  values: 'Its options look like the values this field holds.',
};

const GROUP_TITLES: Record<FieldMapGroupKey, string> = {
  attention: 'Needs your attention',
  matched: 'Matched automatically',
  confirmed: 'Confirmed',
  notMapped: 'Not mapped',
};

/** What leaving a field unmapped costs, where that is worth saying on its own. */
const FIELD_COSTS: Record<string, string> = {
  birthDate: 'Without a date of birth, most contacts cannot be scored.',
  doNotContact: 'Without it, contacts who opted out cannot be left off your list.',
  email: 'Without an email, Waterlily cannot tell which contacts are already clients.',
  netWorth: 'Without net worth, fit is judged on fewer details.',
  isDeceased: 'Without it, deceased contacts cannot be left off your list.',
  recordType: 'Without it, the record types you chose to leave out cannot be left off your list.',
  isInactive: 'Without it, inactive or terminated contacts cannot be left off your list.',
  onLtcClaim: 'Without it, contacts already on a long-term care claim cannot be left off your list.',
  ltcCoverageInForce: 'Without it, contacts who already have long-term care coverage cannot be left off your list.',
};

const ROLE_COSTS: Record<Prospecting.CanonicalFieldRole, string> = {
  identity: 'Used to recognise the contact.',
  fit: 'Used to judge how closely a contact fits. Scoring works without it, on fewer details.',
  disqualifier: 'Without it, contacts this applies to stay on your list.',
};

export const fieldMapCopy = {
  pageTitle: (crm: string) => `${crm} field mapping`,
  back: 'Back',
  refreshFields: 'Refresh field list',
  refreshingFields: 'Refreshing…',

  loadingFields: (crm: string) => `Reading your field list from ${crm}…`,
  fieldsCardTitle: 'Contact fields',
  fieldsIntro: (crm: string) =>
    `Waterlily matches the ${crm} fields it recognises. Check the matches, then confirm them in one step.`,
  fieldsReadable: (count: number, crm: string) =>
    `Only the ${count} fields your ${crm} user can read are listed. If something is missing, your ${crm} administrator controls access to it.`,
  fieldsFound: (count: number, crm: string) =>
    `${count} fields were found. ${crm} does not tell us which of them your own permissions cover, so a field listed here may still be refused when we read it.`,

  summary: (ready: number, total: number) => `${ready} of ${total} fields are ready.`,
  summaryPending: (count: number) =>
    count === 1 ? '1 match is waiting for you to confirm.' : `${count} matches are waiting for you to confirm.`,

  groupTitle: (group: FieldMapGroupKey) => GROUP_TITLES[group],
  groupHint: (group: FieldMapGroupKey, crm: string): string => {
    switch (group) {
      case 'attention':
        return 'Each of these needs one choice from you before it can be used.';
      case 'matched':
        return `Waterlily found these in ${crm}. Nothing is read through them until you confirm.`;
      case 'confirmed':
        return 'These are in use.';
      case 'notMapped':
        return `Nothing is read for these. Choose a ${crm} field if you keep the value somewhere.`;
    }
  },
  showGroup: 'Show',
  hideGroup: 'Hide',

  matchReason: (matchedOn: string | null) => (matchedOn ? (MATCH_REASONS[matchedOn] ?? null) : null),
  derived: (crm: string) => `Filled in from related records in ${crm}. Map a field only if you keep it on the contact.`,
  sourceMissing: (field: string, crm: string) =>
    `${field} is no longer in ${crm}, so nothing is being read here. Choose another field or leave this unmapped.`,
  noMatch: (crm: string) => `No matching field was found in ${crm}.`,
  leftUnmapped: 'You chose to leave this unmapped.',
  cost: (canonicalField: string, role: Prospecting.CanonicalFieldRole | null) =>
    FIELD_COSTS[canonicalField] ?? (role ? ROLE_COSTS[role] : ''),
  suggestionLead: 'Suggested:',
  useSuggestion: 'Use this field',

  status: {
    confirmed: 'Confirmed',
    derived: 'Filled in for you',
    check: 'Check this match',
    changed: 'Not saved yet',
    needsUnit: 'Choose a unit',
    needsRule: 'Say what means yes',
    sourceMissing: 'Field removed',
    notMapped: 'Not mapped',
  },

  picker: {
    placeholder: (crm: string) => `Choose a ${crm} field`,
    search: 'Search by name or label…',
    none: 'No field matches that search.',
    unmapped: 'Leave unmapped',
    suggested: 'Suggested',
    ownFields: 'Contact fields',
    groupFields: (group: string) => `${group} fields`,
    showAll: 'Show fields of every type',
    showAllHint: 'Fields of a type that does not usually hold this value are hidden.',
    missing: (field: string) => `${field} (no longer available)`,
  },

  unit: {
    question: 'Which unit is this stored in?',
    label: (field: string) => `Unit for ${field}`,
    currencyHint: (crm: string) => `${crm} stores this as a currency amount, so this is usually Dollars.`,
  },
  yesRule: {
    question: 'Which value means "yes"?',
    label: (field: string) => `What means yes for ${field}`,
    anyValue: 'Any value means yes',
    pickValue: 'One value means yes',
    oneValue: (value: string) => `"${value}" means yes`,
    unread: 'This field is not a yes or no, so it is not read until you say what means yes.',
    dateHint: 'For a date such as a date of death, any value usually means yes.',
  },

  actions: {
    confirm: (count: number, includesRemoval = false) => {
      const noun = includesRemoval ? 'change' : 'mapping';
      return `Confirm ${count} ${noun}${count === 1 ? '' : 's'}`;
    },
    saving: 'Saving…',
    discard: 'Discard changes',
    allConfirmed: 'All mapped fields are confirmed.',
    nothingMapped: 'Nothing is mapped yet.',
    needsConsent: 'Agree above to let Waterlily read your contacts before you confirm.',
    needsUnits: (count: number) =>
      count === 1
        ? 'Choose a unit for the 1 number field above before you confirm.'
        : `Choose a unit for the ${count} number fields above before you confirm.`,
    ready: 'Confirming starts a sync of your contacts with these fields.',
    saved: 'Mapping saved. Your contacts are syncing with the new fields.',
  },

  alerts: {
    fieldsTitle: 'The field list could not be refreshed',
    fieldsFirstTitle: 'The field list could not be read',
    saveTitle: 'Your mapping was not saved',
    syncTitle: 'The sync could not be started',
    settingsTitle: 'The schedule was not saved',
    consentTitle: 'Your agreement was not saved',
    duplicatesTitle: 'Your choice was not saved',
    tryAgain: 'Try again',
  },

  sync: {
    unavailable: (crm: string) => `Could not check when your ${crm} contacts were last synced.`,
    running: (crm: string) => `Syncing your ${crm} contacts…`,
    progress: (count: number) =>
      count === 1 ? '1 contact read so far.' : `${count.toLocaleString('en-US')} contacts read so far.`,
    background: 'This continues if you leave the page.',
    last: (crm: string, when: string) => `Your ${crm} contacts were last synced on ${when}.`,
    partial: 'That sync stopped early, so some contacts were not reached.',
    failed: (crm: string) =>
      `The last sync of your ${crm} contacts did not finish. Try again, or contact support if it keeps happening.`,
    never: (crm: string) => `Your ${crm} contacts have not been synced yet. Confirm your field mapping to start.`,
    schedule: 'Check for changes',
    syncNow: 'Sync contacts now',
    starting: 'Starting…',
    syncing: 'Syncing…',
    locked: 'Agree above to let Waterlily read your contacts before syncing them.',
    finished: (crm: string, count: number) =>
      `Finished syncing ${count.toLocaleString('en-US')} ${crm} ${count === 1 ? 'contact' : 'contacts'}.`,
    finishedPartial: (crm: string) => `The ${crm} sync stopped early. Some contacts were not reached.`,
    finishedFailed: (crm: string) => `The ${crm} sync did not finish.`,
  },

  consent: {
    saving: 'Saving your agreement…',
  },

  duplicates: {
    title: (count: number) => `Possible duplicates (${count})`,
    intro:
      'These contacts share an email address, so they may be the same person held twice, often because they exist in more than one of your systems. Keeping both is fine if they are different people.',
    changed: 'Changed since you decided',
    unnamed: 'Unnamed contact',
    keep: 'Keep this one',
    kept: 'Kept',
    keepBoth: 'They are different people. Keep both.',
  },
};
